/**
 * Icons Showcase Slider - Lightweight implementation
 * Uses native browser APIs and modern JavaScript
 */
class IconsShowcaseSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.dots = [];
    this.currentIndex = 0;
    this.totalSlides = 0;
    this.isTransitioning = false;
  }

  connectedCallback() {
    // Small delay to ensure DOM is fully connected
    setTimeout(() => this.init(), 50);
  }

  init() {
    this.slider = this.querySelector('[id^="Slider-"]');
    this.totalSlides = this.querySelectorAll('[id^="Slide-"]').length;
    
    // Find dots in the parent section (they're outside this custom element)
    const parentSection = this.closest('.icons-showcase');
    this.dots = parentSection ? [...parentSection.querySelectorAll('.icons-showcase-dot')] : [];
    
    if (!this.slider || this.totalSlides === 0) return;
    
    this.bindEvents();
    this.updateDots();
    
    // Use Intersection Observer for better performance
    this.setupIntersectionObserver();
  }

  bindEvents() {
    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
      dot.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.goToSlide(index);
        }
      });
    });

    // Button navigation
    const prevBtn = this.querySelector('button[name="previous"]');
    const nextBtn = this.querySelector('button[name="next"]');
    
    if (prevBtn) prevBtn.addEventListener('click', () => this.goToPrev());
    if (nextBtn) nextBtn.addEventListener('click', () => this.goToNext());

    // Keyboard navigation
    this.slider.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.goToPrev();
      if (e.key === 'ArrowRight') this.goToNext();
    });

    // Handle resize with debouncing
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.handleResize(), 150);
    });

    // Always add scroll listener as backup for mobile
    if (window.innerWidth <= 990) {
      this.setupScrollListener();
    }
  }

  setupIntersectionObserver() {
    if (!window.IntersectionObserver) {
      // Fallback for browsers without IntersectionObserver
      this.setupScrollListener();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (this.isTransitioning) return;

        let mostVisible = null;
        let maxRatio = 0;

        entries.forEach((entry) => {
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            mostVisible = entry.target;
          }
        });

        if (mostVisible && maxRatio > 0.5) {
          const slides = [...this.querySelectorAll('[id^="Slide-"]')];
          const newIndex = slides.indexOf(mostVisible);
          if (newIndex !== -1 && newIndex !== this.currentIndex) {
            this.currentIndex = newIndex;
            this.updateDots();
          }
        }
      },
      {
        root: this.slider,
        rootMargin: '0px',
        threshold: [0.1, 0.3, 0.5, 0.7, 0.9]
      }
    );

    this.querySelectorAll('[id^="Slide-"]').forEach(slide => {
      observer.observe(slide);
    });
  }

  setupScrollListener() {
    if (!this.slider) return;
    
    let scrollTimer;
    this.slider.addEventListener('scroll', () => {
      if (this.isTransitioning) return;
      
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        this.updateCurrentSlideFromScroll();
      }, 100);
    }, { passive: true });
  }

  updateCurrentSlideFromScroll() {
    if (!this.slider || this.isTransitioning) return;
    
    const slides = [...this.querySelectorAll('[id^="Slide-"]')];
    const scrollLeft = this.slider.scrollLeft;
    const containerWidth = this.slider.offsetWidth;
    const containerCenter = scrollLeft + (containerWidth / 2);
    
    let closestSlide = 0;
    let minDistance = Infinity;
    
    slides.forEach((slide, index) => {
      const slideLeft = slide.offsetLeft;
      const slideWidth = slide.offsetWidth;
      const slideCenter = slideLeft + (slideWidth / 2);
      const distance = Math.abs(slideCenter - containerCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestSlide = index;
      }
    });
    
    if (closestSlide !== this.currentIndex) {
      this.currentIndex = closestSlide;
      this.updateDots();
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides || this.isTransitioning) return;
    
    this.isTransitioning = true;
    this.currentIndex = index;
    
    const slides = this.querySelectorAll('[id^="Slide-"]');
    const targetSlide = slides[index];
    
    if (targetSlide) {
      targetSlide.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
    
    this.updateDots();
    
    // Reset transition state
    setTimeout(() => {
      this.isTransitioning = false;
    }, 500);
  }

  goToPrev() {
    const newIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
    this.goToSlide(newIndex);
  }

  goToNext() {
    const newIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;
    this.goToSlide(newIndex);
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-pressed', isActive.toString());
      dot.setAttribute('aria-label', `Go to slide ${index + 1} of ${this.totalSlides}`);
    });
  }

  handleResize() {
    // Reset to first slide on significant layout changes
    if (window.innerWidth > 990 && this.currentIndex > 0) {
      this.currentIndex = 0;
      this.updateDots();
    }
  }
}

// Register the custom element
if (!customElements.get('icons-showcase-slider')) {
  customElements.define('icons-showcase-slider', IconsShowcaseSlider);
}